// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import invariant from 'invariant';
import styled from 'styled-components';
import {
  removeArrayIndex,
  shiftArrayItemUp,
  shiftArrayItemDown,
  wrapPromise,
  moveArrayItem,
  updateArrayItem,
  range,
  uuid,
  objectEmpty,
  filterObject
} from 'utils/functional';
import {
  ChevronUpButton,
  ChevronDownButton,
  LinkButton,
  ExpandButton
} from 'components/Button';
import { H5 } from 'components/Text';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Container from 'components/Container';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import DeleteButton from 'components/DeleteButton';
import ShowConfirmationModal from 'containers/ShowConfirmationModal';
import AccordionGroupContainer from 'containers/AccordionGroupContainer';
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc';
import { isBrowser } from 'utils/browser';
import Field from './Field';
import { colors, gutterWidth } from '../../theme';

const { useMemo, useCallback } = React;

const MultiInputContainer = styled(Box)`
  .MultiInput__sorting {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      z-index: -1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + ${gutterWidth / 2}px);
      height: calc(100% + ${gutterWidth / 2}px);
      border-radius: 8px;
      box-shadow: 0 3px 7px rgba(0, 0, 0, 0.075);
      background: white;
    }
    hr {
      display: none;
    }
  }
`;

const SortableIcon = SortableHandle(() => (
  <Box py="3px">
    <Icon
      display="block"
      name="move"
      width={14}
      height={14}
      fill={colors.greyishBrown}
    />
  </Box>
));

const SortableBox = styled(
  ({
    collapsible,
    toggled,
    header,
    children,
    toggle,
    label,
    itemIndex,
    ...props
  }: {
    collapsible?: boolean,
    toggled: boolean,
    header: React.Node,
    children: React.Node,
    toggle: Function,
    label: string,
    itemIndex: number
  }) => (
    <>
      {itemIndex > 0 && <Divider dashed my={24} />}
      <Box {...props} position="relative">
        {collapsible && (
          <Box position="absolute" top="3px" left="-2px">
            <ExpandButton
              display="block"
              onClick={toggle}
              data-qa-id="expand-section"
              isActive={toggled}
              title={sprintf(getText('%(toggle)s %(name)s'), {
                toggle: toggled ? 'Collapse' : 'Expand',
                name: label
              })}
              blue
              small
            />
          </Box>
        )}
        <Box pl={collapsible ? 48 : 0}>{header}</Box>
        {toggled && children}
      </Box>
    </>
  )
)`
  * {
    :last-child {
      margin-bottom: 0;
    }
  }
`;

const SortableItem = SortableElement(SortableBox);
const SortableGrid = SortableContainer(Box);

const MovementButtons = ({
  moveInputGroupUp,
  moveInputGroupDown,
  removeInputGroup,
  confirmationText,
  showUpArrow,
  showDownArrow,
  disableRemove,
  hideRemove
}: {|
  moveInputGroupUp: number => any,
  moveInputGroupDown: number => any,
  removeInputGroup: number => any,
  confirmationText: ?string,
  showUpArrow: boolean,
  showDownArrow: boolean,
  disableRemove: boolean,
  hideRemove: boolean
|}) => (
  <Flex justifyContent="flex-end" alignItems="flex-start" flexDirection="row">
    {(showDownArrow || showUpArrow) && (
      <>
        <Box
          display="inline-flex"
          alignItems="center"
          pr="20px"
          mr="20px"
          borderRight={!hideRemove && `1px solid ${colors.silver}`}
        >
          {showUpArrow && (
            <ChevronUpButton
              p="0 4px"
              onClick={moveInputGroupUp}
              data-qa-id="uparrow"
            />
          )}
          {showDownArrow && (
            <ChevronDownButton
              p="0 4px"
              onClick={moveInputGroupDown}
              data-qa-id="downarrow"
            />
          )}
        </Box>
        <Box
          display="inline-flex"
          alignItems="center"
          pr="20px"
          mr="20px"
          borderRight={`1px solid ${colors.silver}`}
        >
          <SortableIcon />
        </Box>
      </>
    )}
    {!hideRemove && (
      <Container display="inline-flex" pt="3px">
        {confirmationText ? (
          <ShowConfirmationModal
            data-qa-id="remove-section-modal"
            type="warning"
            title={getText('Remove Section')}
            buttonText={getText('Remove')}
            body={confirmationText}
            onConfirm={wrapPromise(removeInputGroup)}
            render={({ showModal }) => (
              <DeleteButton
                disabled={disableRemove}
                data-qa-id="remove-section"
                onClick={showModal}
              />
            )}
          />
        ) : (
          <DeleteButton
            disabled={disableRemove}
            data-qa-id="remove-section"
            onClick={removeInputGroup}
          />
        )}
      </Container>
    )}
  </Flex>
);

const MovementButtonsMemo = React.memo(MovementButtons);

const MultiInputGroup = <T>({
  name,
  title,
  inputGroupCount,
  minInputGroups,
  maxInputGroups,
  renderInputGroup,
  renderTitle,
  qaId,
  addText,
  defaultData,
  confirmationText,
  updateValueWith,
  sections,
  values,
  errors,
  collapsible
}: {|
  name: string,
  title?: string,
  inputGroupCount: number,
  minInputGroups: number,
  maxInputGroups?: number,
  renderInputGroup: (
    namePrefix: string,
    section: T,
    updateValue: ((T) => T) => void,
    index: number,
    values: Object
  ) => React.Node,
  renderTitle?: (namePrefix: string, section: T, index: number) => React.Node,
  qaId: string,
  addText: string,
  defaultData?: Object,
  confirmationText?: string,
  'data-qa-id'?: string,
  updateValueWith: Function,
  sections: Array<T>,
  values: Object,
  errors?: APIErrors,
  collapsible?: boolean
|}) => {
  const addInputGroup = useCallback(
    toggle => {
      updateValueWith(v => v.concat(defaultData || [{ tempId: uuid() }]));
      toggle(sections.length);
    },
    [defaultData, sections.length, updateValueWith]
  );
  const moveInputGroupUp = useMemo(
    () =>
      range(sections.length).map(i => () =>
        updateValueWith(v => shiftArrayItemUp(v, i))
      ),
    [sections.length, updateValueWith]
  );
  const moveInputGroupDown = useMemo(
    () =>
      range(sections.length).map(i => () =>
        updateValueWith(v => shiftArrayItemDown(v, i))
      ),
    [sections.length, updateValueWith]
  );
  const removeInputGroup = useMemo(
    () =>
      range(sections.length).map(i => () =>
        updateValueWith(v => removeArrayIndex(v, i))
      ),
    [sections.length, updateValueWith]
  );
  const sortHandler = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
      updateValueWith(v => moveArrayItem(v, oldIndex, newIndex));
    },
    [updateValueWith]
  );

  const updateSectionValue = index => f =>
    updateValueWith(cs => updateArrayItem(cs, index, f(cs[index])));

  invariant(name, 'name is required');

  return (
    <MultiInputContainer
      data-qa-id={qaId}
      data-qa-group-count={inputGroupCount}
      id={`${name}__root`}
    >
      <AccordionGroupContainer
        defaultValue={0}
        render={(toggle, currentSection) => (
          <>
            <SortableGrid
              onSortEnd={sortHandler}
              distance={5}
              useDragHandle
              helperClass="MultiInput__sorting"
              helperContainer={
                isBrowser()
                  ? document.getElementById(`${name}__root`)
                  : undefined
              }
            >
              {sections.map((section, index) => {
                const atMinInputGroups = inputGroupCount <= minInputGroups;
                const actualTitle = renderTitle
                  ? renderTitle(`${name}.${index}.`, section, index)
                  : title
                  ? `${title} Section ${index + 1}`
                  : '';
                // Errors for any sub-fields in this multiinput group
                const itemErrors =
                  errors &&
                  filterObject(errors, k => k.includes(`${name}.${index}`));
                return (
                  <SortableItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    index={index}
                    itemIndex={index}
                    data-qa-id={`${qaId}-${index}`}
                    position="relative"
                    collapsible={collapsible}
                    toggled={
                      !collapsible ||
                      currentSection === index ||
                      !objectEmpty(itemErrors)
                    }
                    toggle={() => toggle(index)}
                    label={title}
                    header={
                      <Box
                        display="flex"
                        width="100%"
                        justifyContent="space-between"
                      >
                        {title && (
                          <H5
                            pr={32}
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            mb={0}
                          >
                            {actualTitle}
                          </H5>
                        )}
                        <MovementButtonsMemo
                          moveInputGroupUp={moveInputGroupUp[index]}
                          moveInputGroupDown={moveInputGroupDown[index]}
                          removeInputGroup={removeInputGroup[index]}
                          confirmationText={confirmationText}
                          showUpArrow={index > 0}
                          showDownArrow={index < sections.length - 1}
                          disableRemove={atMinInputGroups}
                          hideRemove={minInputGroups === maxInputGroups}
                        />
                      </Box>
                    }
                  >
                    <Box mt={24}>
                      {renderInputGroup(
                        `${name}.${index}.`,
                        section,
                        updateSectionValue(index),
                        index,
                        values
                      )}
                    </Box>
                  </SortableItem>
                );
              })}
            </SortableGrid>

            {!maxInputGroups || sections.length < maxInputGroups ? (
              <>
                <Divider dashed my={36} />
                <Box textAlign="center">
                  <LinkButton
                    disabled={inputGroupCount === maxInputGroups}
                    data-qa-id={`${qaId}-add`}
                    onClick={() => addInputGroup(toggle)}
                  >
                    {addText}
                  </LinkButton>
                </Box>
              </>
            ) : (
              <Box data-qa-id={`${qaId}-add`} />
            )}
          </>
        )}
      />
    </MultiInputContainer>
  );
};

export default <T>({
  name,
  title,
  renderInputGroup,
  renderTitle,
  addText,
  defaultData,
  ...props
}: {
  name: string,
  title?: string,
  inputGroupCount?: number,
  minInputGroups: number,
  maxInputGroups?: number,
  renderInputGroup: (
    namePrefix: string,
    section: T,
    updateValue: ((T) => T) => void,
    index: number
  ) => React.Node,
  renderTitle?: (namePrefix: string, section: T, index: number) => React.Node,
  addText?: string,
  defaultData?: Object,
  confirmationText?: string,
  errors?: APIErrors,
  'data-qa-id'?: string
}) => (
  <Field
    name={name}
    inputProps={props}
    renderInput={({ value, updateValueWith, values, qaId }) => (
      <MultiInputGroup
        name={name}
        qaId={qaId}
        addText={
          addText ||
          /* TRANSLATORS: Text to add form section */ getText('+ Add Section')
        }
        defaultData={defaultData}
        title={title}
        inputGroupCount={props.inputGroupCount || value ? value.length : 0}
        updateValueWith={updateValueWith}
        values={values}
        sections={value || []}
        renderInputGroup={renderInputGroup}
        renderTitle={renderTitle}
        data-qa-id={props['data-qa-id']}
        {...props}
      />
    )}
  />
);
